"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VoiceNetworkMonitor = void 0;
exports.tuneOpusSdp = tuneOpusSdp;
const realtimeConfig_1 = require("../config/realtimeConfig");
const LEVEL_RANK = { good: 0, fair: 1, poor: 2, bad: 3, offline: 4 };
const AUDIO_KBPS = { good: 128, fair: 96, poor: 64, bad: 48, offline: 32 };
const VIDEO_SCALE = { good: 1, fair: 1.5, poor: 2.5, bad: 4, offline: 4 };
const VIDEO_BITRATE_FACTOR = { good: 1, fair: 0.6, poor: 0.3, bad: 0.12, offline: 0.1 };
const JITTER_TARGET_MS = { good: 0, fair: 120, poor: 250, bad: 450, offline: 450 };
/**
 * Her 2 sn'de WebRTC istatistiklerini okur, ağ kalitesini hesaplar ve
 * bağlantı yavaşladığında SES KESİLMESİN diye gönderim ayarlarını otomatik düşürür:
 *  - Opus bitrate düşer, video çözünürlük/bitrate/fps düşer, kötü ağda kamera durur
 *  - Alıcı jitter buffer büyür (kesilme yerine biraz gecikme tercih edilir)
 * Düzelince kademeli olarak eski kaliteye döner (histerezis ile titreme yok).
 */
class VoiceNetworkMonitor {
    constructor(host) {
        this.host = host;
        this.timer = null;
        this.prev = new Map();
        this.level = 'good';
        this.badStreak = 0;
        this.goodStreak = 0;
        this.lastApplied = '';
        this.last = null;
        this.baseVideoBitrate = new WeakMap();
        this.onOffline = () => {
            this.level = 'offline';
            this.publish([], 0, 0, 0, 0, 0, 'unknown');
        };
        this.onOnline = () => {
            this.level = 'poor';
            this.goodStreak = 0;
        };
    }
    start() {
        if (this.timer)
            return;
        this.timer = setInterval(() => this.tick().catch(() => { }), 2000);
        window.addEventListener('offline', this.onOffline);
        window.addEventListener('online', this.onOnline);
    }
    stop() {
        if (this.timer)
            clearInterval(this.timer);
        this.timer = null;
        window.removeEventListener('offline', this.onOffline);
        window.removeEventListener('online', this.onOnline);
        this.prev.clear();
        this.level = 'good';
        this.badStreak = 0;
        this.goodStreak = 0;
        this.lastApplied = '';
        this.last = null;
    }
    getLast() {
        return this.last;
    }
    async tick() {
        const peers = this.host.getPeers();
        if (!navigator.onLine) {
            this.level = 'offline';
        }
        const peerStats = [];
        let maxRtt = 0;
        let maxLoss = 0;
        let maxJitter = 0;
        let sumIn = 0;
        let sumOut = 0;
        for (const [socketId, pc] of peers.entries()) {
            if (pc.connectionState === 'closed')
                continue;
            let stats;
            try {
                stats = await pc.getStats();
            }
            catch {
                continue;
            }
            let rtt = null;
            let route = 'unknown';
            let lost = 0;
            let recv = 0;
            let jitter = 0;
            let inBytes = 0;
            let outBytes = 0;
            let remoteFraction = 0;
            const byId = {};
            stats.forEach((r) => (byId[r.id] = r));
            stats.forEach((r) => {
                if (r.type === 'candidate-pair' && (r.nominated || r.selected) && r.state === 'succeeded') {
                    if (typeof r.currentRoundTripTime === 'number')
                        rtt = r.currentRoundTripTime * 1000;
                    const l = byId[r.localCandidateId];
                    const rem = byId[r.remoteCandidateId];
                    const lt = l?.candidateType;
                    const rt = rem?.candidateType;
                    route = lt === 'relay' || rt === 'relay' ? 'relay' : lt ? 'p2p' : 'unknown';
                }
                else if (r.type === 'inbound-rtp' && !r.isRemote) {
                    if (r.kind === 'audio') {
                        lost += r.packetsLost || 0;
                        recv += r.packetsReceived || 0;
                        if (typeof r.jitter === 'number')
                            jitter = Math.max(jitter, r.jitter * 1000);
                    }
                    inBytes += r.bytesReceived || 0;
                }
                else if (r.type === 'outbound-rtp' && !r.isRemote) {
                    outBytes += r.bytesSent || 0;
                }
                else if (r.type === 'remote-inbound-rtp') {
                    if (typeof r.fractionLost === 'number')
                        remoteFraction = Math.max(remoteFraction, r.fractionLost * 100);
                    if (rtt === null && typeof r.roundTripTime === 'number')
                        rtt = r.roundTripTime * 1000;
                }
            });
            const now = performance.now();
            const p = this.prev.get(socketId);
            let lossPct = remoteFraction;
            let inKbps = 0;
            let outKbps = 0;
            if (p) {
                const dLost = Math.max(0, lost - p.lost);
                const dRecv = Math.max(0, recv - p.recv);
                const total = dLost + dRecv;
                if (total > 0)
                    lossPct = Math.max(lossPct, (dLost / total) * 100);
                const dt = Math.max(0.5, (now - p.ts) / 1000);
                inKbps = (Math.max(0, inBytes - p.inBytes) * 8) / 1000 / dt;
                outKbps = (Math.max(0, outBytes - p.outBytes) * 8) / 1000 / dt;
            }
            this.prev.set(socketId, { lost, recv, inBytes, outBytes, ts: now });
            const meta = this.host.getMeta(socketId);
            peerStats.push({
                socketId,
                userId: meta?.userId,
                displayName: meta?.displayName,
                route,
                rttMs: rtt === null ? null : Math.round(rtt),
                lossPct: Math.round(lossPct * 10) / 10,
                jitterMs: Math.round(jitter),
                inKbps: Math.round(inKbps),
                outKbps: Math.round(outKbps),
                state: pc.connectionState,
            });
            maxRtt = Math.max(maxRtt, rtt ?? 0);
            maxLoss = Math.max(maxLoss, lossPct);
            maxJitter = Math.max(maxJitter, jitter);
            sumIn += inKbps;
            sumOut += outKbps;
        }
        // Seviye hesabı
        let measured = 'good';
        if (maxLoss >= 12 || maxRtt >= 600 || maxJitter >= 120)
            measured = 'bad';
        else if (maxLoss >= 5 || maxRtt >= 300 || maxJitter >= 60)
            measured = 'poor';
        else if (maxLoss >= 2 || maxRtt >= 150 || maxJitter >= 30)
            measured = 'fair';
        if (this.level !== 'offline' || navigator.onLine) {
            if (LEVEL_RANK[measured] > LEVEL_RANK[this.level]) {
                // Kötüleşme: 2 ardışık ölçüm (4 sn) — veya hemen "bad" ise 1
                this.badStreak++;
                this.goodStreak = 0;
                if (this.badStreak >= 2 || measured === 'bad') {
                    this.level = measured;
                    this.badStreak = 0;
                }
            }
            else if (LEVEL_RANK[measured] < LEVEL_RANK[this.level]) {
                // İyileşme: 3 ardışık ölçüm (6 sn) sonra bir kademe yukarı
                this.goodStreak++;
                this.badStreak = 0;
                if (this.goodStreak >= 3) {
                    const next = Math.max(LEVEL_RANK[measured], LEVEL_RANK[this.level] - 1);
                    this.level = Object.keys(LEVEL_RANK).find((k) => LEVEL_RANK[k] === next) || 'good';
                    this.goodStreak = 0;
                }
            }
            else {
                this.badStreak = 0;
                this.goodStreak = 0;
            }
        }
        const routes = new Set(peerStats.map((p) => p.route).filter((r) => r !== 'unknown'));
        const route = routes.size === 0 ? 'unknown' : routes.size > 1 ? 'mixed' : Array.from(routes)[0];
        if ((0, realtimeConfig_1.getAdaptiveQualityEnabled)()) {
            await this.applyAdaptive();
        }
        else if (this.lastApplied !== 'off') {
            await this.resetAdaptive();
        }
        this.publish(peerStats, maxRtt, maxLoss, maxJitter, sumIn, sumOut, route);
    }
    publish(peers, rtt, loss, jitter, inKbps, outKbps, route) {
        const lvl = this.level;
        const q = {
            level: lvl,
            rttMs: Math.round(rtt),
            lossPct: Math.round(loss * 10) / 10,
            jitterMs: Math.round(jitter),
            inKbps: Math.round(inKbps),
            outKbps: Math.round(outKbps),
            route,
            adaptive: {
                audioKbps: AUDIO_KBPS[lvl],
                videoScale: VIDEO_SCALE[lvl],
                videoActive: !(lvl === 'bad' || lvl === 'offline') || !this.host.isCameraOnlyVideo(),
                jitterTargetMs: JITTER_TARGET_MS[lvl],
            },
            peers,
            at: Date.now(),
        };
        this.last = q;
        this.host.emit('network-quality', q);
        // Eski UI (voiceStats) uyumluluğu: 1 (mükemmel) .. 5 (çok kötü)
        const map = { good: 1, fair: 3, poor: 4, bad: 5, offline: 6 };
        this.host.emit('stats', { rtt: Math.round(rtt), downlinkNetworkQuality: map[lvl] });
    }
    async applyAdaptive() {
        const lvl = this.level;
        const sig = `${lvl}|${this.host.getPeers().size}|${this.host.isCameraOnlyVideo()}`;
        if (sig === this.lastApplied)
            return;
        this.lastApplied = sig;
        for (const pc of this.host.getPeers().values()) {
            try {
                // GÖNDERİM
                for (const sender of pc.getSenders()) {
                    const kind = sender.track?.kind;
                    if (!kind)
                        continue;
                    const params = sender.getParameters();
                    if (!params.encodings || params.encodings.length === 0)
                        params.encodings = [{}];
                    const enc = params.encodings[0];
                    if (kind === 'audio') {
                        enc.maxBitrate = AUDIO_KBPS[lvl] * 1000;
                        enc.priority = 'high';
                        enc.networkPriority = 'high';
                    }
                    else if (kind === 'video') {
                        const manual = this.host.getManualQuality ? this.host.getManualQuality() : 'auto';
                        if (manual && manual !== 'auto') {
                            // User has manually locked video quality (1080p, 720p, etc.) - do not override!
                            continue;
                        }
                        if (!this.baseVideoBitrate.has(sender)) {
                            this.baseVideoBitrate.set(sender, typeof enc.maxBitrate === 'number' ? enc.maxBitrate : 2500000);
                        }
                        const base = this.baseVideoBitrate.get(sender);
                        enc.maxBitrate = Math.max(80000, Math.round(base * VIDEO_BITRATE_FACTOR[lvl]));
                        enc.scaleResolutionDownBy = VIDEO_SCALE[lvl];
                        enc.maxFramerate = lvl === 'good' ? 30 : lvl === 'fair' ? 24 : lvl === 'poor' ? 15 : 8;
                        // Kötü ağda SES korunsun: sadece kamera durdurulur (ekran paylaşımı sürer)
                        enc.active = !((lvl === 'bad' || lvl === 'offline') && this.host.isCameraOnlyVideo());
                        enc.priority = 'low';
                        params.degradationPreference = 'maintain-framerate';
                    }
                    await sender.setParameters(params).catch(() => { });
                }
                // ALMA: jitter buffer'ı büyüt → kesilme yerine küçük gecikme
                for (const receiver of pc.getReceivers()) {
                    const r = receiver;
                    const ms = JITTER_TARGET_MS[lvl];
                    try {
                        if ('jitterBufferTarget' in r)
                            r.jitterBufferTarget = ms === 0 ? null : ms;
                        if ('playoutDelayHint' in r)
                            r.playoutDelayHint = ms === 0 ? null : ms / 1000;
                    }
                    catch { }
                }
            }
            catch { }
        }
    }
    async resetAdaptive() {
        this.lastApplied = 'off';
        const prevLevel = this.level;
        this.level = 'good';
        await this.applyAdaptive();
        this.level = prevLevel;
        this.lastApplied = 'off';
    }
}
exports.VoiceNetworkMonitor = VoiceNetworkMonitor;
/** Opus'u konuşma ve ekran paylaşım yayınları için optimize eder: Stereo + Yüksek Bitrate (128 kbps) + DTX kapalı (sesin derinden gelmemesi ve kesilmemesi için). */
function tuneOpusSdp(sdp) {
    try {
        const m = sdp.match(/a=rtpmap:(\d+) opus\/48000\/2/);
        if (!m)
            return sdp;
        const pt = m[1];
        const wanted = 'useinbandfec=1;usedtx=0;stereo=1;sprop-stereo=1;maxaveragebitrate=128000;maxplaybackrate=48000;cbr=1;minptime=10';
        const re = new RegExp(`a=fmtp:${pt} ([^\\r\\n]*)`);
        const fm = sdp.match(re);
        if (fm) {
            const existing = new Map();
            fm[1].split(';').forEach((kv) => {
                const [k, v] = kv.split('=');
                if (k)
                    existing.set(k.trim(), v ?? '');
            });
            wanted.split(';').forEach((kv) => {
                const [k, v] = kv.split('=');
                existing.set(k, v);
            });
            const merged = Array.from(existing.entries())
                .map(([k, v]) => (v === '' ? k : `${k}=${v}`))
                .join(';');
            return sdp.replace(re, `a=fmtp:${pt} ${merged}`);
        }
        return sdp.replace(m[0], `${m[0]}\r\na=fmtp:${pt} ${wanted}`);
    }
    catch {
        return sdp;
    }
}
